/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { defaultFilterValues, defaultListingPageSize } from "Constants/M365CopilotBenefitConstants";

import { IM365CopilotBenefitState } from "./IM365CopilotBenefitState";
import {
  IAddM365CopilotFilterValue,
  IRemoveM365CopilotFilterValue,
  ISearchM365CopilotBenefit,
  ISearchM365CopilotBenefitFail,
  ISearchM365CopilotBenefitSuccess,
  IUpdateM365CopilotCurrentPage,
  IUpdateM365CopilotFilterValue,
  IUpdateM365CopilotPageSize,
  M365CopilotBenefitActionTypes,
} from "./M365CopilotBenefitAction";
import {
  M365_COPILOT_BENEFITS_SEARCH,
  M365_COPILOT_BENEFITS_SEARCH_FAILURE,
  M365_COPILOT_BENEFITS_SEARCH_RESET,
  M365_COPILOT_BENEFITS_SEARCH_SUCCESS,
  M365_COPILOT_FILTER_VALUE_ADD,
  M365_COPILOT_FILTER_VALUE_REMOVE,
  M365_COPILOT_FILTER_VALUE_UPDATE,
  UPDATE_M365_COPILOT_LISTING_CURRENT_PAGE,
  UPDATE_M365_COPILOT_LISTING_PAGE_SIZE,
} from "./M365CopilotBenefitActionType";

const INITIAL_STATE: IM365CopilotBenefitState = {
  benefits: [],
  isLoading: false,
  loadBenefitsError: "",
  searchText: "",
  filteredCount: 0,
  pageSize: defaultListingPageSize,
  currentPage: 1,
  m365CopilotFilterValues: defaultFilterValues,
};

const applySearchM365CopilotBenefits = (
  state: IM365CopilotBenefitState,
  action: ISearchM365CopilotBenefit
): IM365CopilotBenefitState => {
  return {
    ...state,
    benefits: [],
    isLoading: true,
    searchText: action.searchText,
    loadBenefitsError: "",
    pageSize: action.pageSize,
    currentPage: action.searchPage,
    currentTenant: action.tenant,
  };
};

const applySearchM365CopilotBenefitsSuccess = (
  state: IM365CopilotBenefitState,
  action: ISearchM365CopilotBenefitSuccess
): IM365CopilotBenefitState => {
  return {
    ...state,
    benefits: action.benefits,
    filteredCount: action.filteredCount,
    isLoading: false,
  };
};

const applySearchM365CopilotBenefitsFail = (
  state: IM365CopilotBenefitState,
  action: ISearchM365CopilotBenefitFail
): IM365CopilotBenefitState => {
  return {
    ...state,
    loadBenefitsError: action.error,
    isLoading: false,
  };
};

const applyResetM365CopilotBenefits = (): IM365CopilotBenefitState => INITIAL_STATE;

const applyAddM365CopilotFilterValue = (
  state: IM365CopilotBenefitState,
  action: IAddM365CopilotFilterValue
): IM365CopilotBenefitState => {
  return {
    ...state,
    m365CopilotFilterValues: state.m365CopilotFilterValues.concat([action.filterValue]),
  };
};

const applyRemoveM365CopilotFilterValue = (
  state: IM365CopilotBenefitState,
  action: IRemoveM365CopilotFilterValue
): IM365CopilotBenefitState => {
  return {
    ...state,
    m365CopilotFilterValues: state.m365CopilotFilterValues.filter(
      (item) =>
        !(
          item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value
        )
    ),
  };
};

const applyUpdateM365CopilotFilterValue = (
  state: IM365CopilotBenefitState,
  action: IUpdateM365CopilotFilterValue
): IM365CopilotBenefitState => {
  return {
    ...state,
    m365CopilotFilterValues: state.m365CopilotFilterValues
      .filter((item) => !(item.fieldName === action.filterValue.fieldName))
      .concat([action.filterValue]),
  };
};

const applyUpdateM365CopilotCurrentPage = (
  state: IM365CopilotBenefitState,
  action: IUpdateM365CopilotCurrentPage
): IM365CopilotBenefitState => {
  return {
    ...state,
    currentPage: action.currentPage,
  };
};

const applyUpdateM365CopilotPageSize = (
  state: IM365CopilotBenefitState,
  action: IUpdateM365CopilotPageSize
): IM365CopilotBenefitState => {
  return {
    ...state,
    pageSize: action.pageSize,
  };
};

function m365CopilotBenefitReducer(
  state = INITIAL_STATE,
  action: M365CopilotBenefitActionTypes
): IM365CopilotBenefitState {
  switch (action.type) {
    case M365_COPILOT_BENEFITS_SEARCH:
      return applySearchM365CopilotBenefits(state, action);

    case M365_COPILOT_BENEFITS_SEARCH_SUCCESS:
      return applySearchM365CopilotBenefitsSuccess(state, action);

    case M365_COPILOT_BENEFITS_SEARCH_FAILURE:
      return applySearchM365CopilotBenefitsFail(state, action);

    case M365_COPILOT_BENEFITS_SEARCH_RESET:
      return applyResetM365CopilotBenefits();

    case M365_COPILOT_FILTER_VALUE_ADD:
      return applyAddM365CopilotFilterValue(state, action);

    case M365_COPILOT_FILTER_VALUE_REMOVE:
      return applyRemoveM365CopilotFilterValue(state, action);

    case M365_COPILOT_FILTER_VALUE_UPDATE:
      return applyUpdateM365CopilotFilterValue(state, action);

    case UPDATE_M365_COPILOT_LISTING_CURRENT_PAGE:
      return applyUpdateM365CopilotCurrentPage(state, action);

    case UPDATE_M365_COPILOT_LISTING_PAGE_SIZE:
      return applyUpdateM365CopilotPageSize(state, action);

    default:
      return state;
  }
}

export default m365CopilotBenefitReducer;
